import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { serializeDateFields } from 'common/utils/time';
import { PaginatedApiPayload, PaginatedApiPayloadV2 } from 'domain/Common';
import {
  type EventDetails,
  type EventDetailsApiResponse,
  type EventGuest,
  type EventImageApiResponse,
  type EventListItem,
  type EventListItemApiResponse,
  type EventQuestionDefinition,
  EventStatus,
  type ListedEventGuest,
  type PublicEventDetails,
  TopicBasicInfo,
} from 'domain/event';
import {
  mapConnectionsFromApi,
  mapEventDetailsFromApi,
  mapEventQuestionsFromApi,
  mapEventWizardFormValuesToApi,
  mapGetEventGuestsParams,
  mapPaginatedEventGuestsFromApi,
  mapPaginatedEventListItemsFromApi,
  mapPublicEventFromApi,
} from 'event/mappers';
import flow from 'lodash/fp/flow';
import { axiosBaseQuery, getApiBaseUrl, getApiErrorMetadata } from 'modules/api/utils';
import { generateFormData } from 'store/utils';

import { omitUndefined } from './utils';

import type {
  AttendEventArgs,
  CreateEventPayload,
  CreateEventQuestionsPayload,
  CreatedEventApiResponse,
  DeleteEventArgs,
  EventStateSnapshot,
  GetEventGuestsPayload,
  GetEventQuestionsPayload,
  GetEventsListPayload,
  MeetingMetricsResponse,
  PublicEventId,
  RegisterForEventArgs,
  StartEventPayload,
  UpdateEventAnswersPayload,
  UpdateEventImagePayload,
  UpdateEventPayload,
  ValidateEventPinPayload,
  WithEventId,
  WithStringableEventId,
} from './types';

// Invalidating the EVENT tag on a register/attend failure resyncs a stale view that caused the
// failure (e.g. a 409 when the event already ended). But a `NOT_COMMUNITY_MEMBER` 403 is expected
// and handled by useEnterEvent (join the community, then retry) — the event itself is unchanged, so
// refetching it there is wasted. Skip invalidation only for that handled case.
const invalidateEventUnlessNotCommunityMember = (error: unknown, id: string | number | undefined) =>
  getApiErrorMetadata(error)?.errorType === 'NOT_COMMUNITY_MEMBER' ? [] : [{ type: 'EVENT' as const, id }];

// Retry with exponential backoff for token requests: 1s, 2s, 2s (total ~5s)
const staggeredBaseQueryWithRetry = retry(axiosBaseQuery({ baseURL: getApiBaseUrl('v2') }), {
  maxRetries: 3,
});

// Used for both the v3 saves and the v3 event read. The tag-category write rides on the event save, so
// a save can come back 502 when the tag service is
// briefly unavailable. Retrying is safe and resumes rather than repeats: RTK Query re-dispatches with
// identical args, so the same Idempotency-Key goes out and the backend skips the event write it already
// did. Only 502 is retried -- a 4xx is our bug or the user's input, and retrying it is pure delay.
//
// NOTE for a manual re-save: a fresh key is minted per user-initiated save, so re-saving after a failed
// CREATE can produce a second event (the 502 does not carry the id of the one that was made). The wizard
// tells the host to open the created event and save its tags instead. Re-saving an UPDATE is always safe.
const withRetryOn502 = retry(axiosBaseQuery({ baseURL: getApiBaseUrl('v3') }), {
  // maxRetries and retryCondition are mutually exclusive in RTK Query's RetryOptions union, so the
  // attempt cap lives inside the condition: 3 retries, 502 only.
  retryCondition: (error, _args, { attempt }) =>
    attempt <= 3 && (error as { status?: number } | undefined)?.status === 502,
});

export const eventApi = createApi({
  reducerPath: 'eventApi',
  baseQuery: axiosBaseQuery({ baseURL: getApiBaseUrl('v2') }),
  tagTypes: ['EVENT', 'ALL_EVENTS', 'PAST_EVENTS', 'UPCOMING_EVENT', 'EVENT_QUESTIONS', 'NATTERING_TOPIC'],
  endpoints: (builder) => ({
    getMeetingState: builder.query<EventStateSnapshot, WithEventId>({
      keepUnusedDataFor: 0,
      query: ({ id }) => `/meeting/${id}/user/state`,
    }),
    getHasAnyEvent: builder.query<boolean, { communityId: number }>({
      providesTags: ['ALL_EVENTS'],
      query: ({ communityId }) => ({
        url: `/event`,
        params: {
          page: 1,
          size: 1,
          communityId,
          states: [EventStatus.Published, EventStatus.Started, EventStatus.Finished],
        },
      }),
      transformResponse: ({ totalCount }: PaginatedApiPayload<EventListItemApiResponse>) => totalCount > 0,
    }),
    getEventsList: builder.query<PaginatedApiPayloadV2<EventListItem>, GetEventsListPayload>({
      providesTags: ['ALL_EVENTS'],
      query: ({ page, pageSize, filters }) => ({
        url: `/event`,
        params: {
          page,
          size: pageSize,
          states: filters?.states?.join(','),
          communityId: filters?.communityId,
          searchText: filters?.searchText,
        },
      }),
      transformResponse: mapPaginatedEventListItemsFromApi,
    }),
    // v3 so the response carries the event's tagCategories, which the wizard seeds its Tags step from.
    // The v3 read 502s rather than returning an empty array when the tag service is unreadable, so that a
    // blank vocabulary can never be loaded and then saved back over real categories -- hence the retry.
    getEventDetails: builder.query<EventDetails, WithStringableEventId>({
      queryFn: async ({ id }, api, extraOptions) => {
        const result = await withRetryOn502({ url: `/event/${id}` }, api, extraOptions);

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: flow(serializeDateFields('startDate'), mapEventDetailsFromApi)(result.data as EventDetailsApiResponse),
        };
      },
      providesTags: (result) => (result ? [{ type: 'EVENT', id: result.id }] : []),
    }),
    getPublicEventDetails: builder.query<PublicEventDetails, PublicEventId>({
      query: ({ id }) => ({
        url: `/event/${id}/register`,
      }),
      transformResponse: flow(serializeDateFields('startTime'), mapPublicEventFromApi),
    }),
    getEventGuests: builder.query<PaginatedApiPayloadV2<EventGuest>, GetEventGuestsPayload>({
      query: ({ id, ...args }) => ({
        url: `/event/${id}/guests`,
        params: mapGetEventGuestsParams(args),
      }),
      transformResponse: mapPaginatedEventGuestsFromApi,
    }),
    getEventGuestsCount: builder.query<number, { id: number | undefined }>({
      query: ({ id }) => ({
        url: `/event/${id}/guests`,
      }),
      transformResponse: (data: PaginatedApiPayloadV2<ListedEventGuest>) => data.totalCount,
    }),
    createEvent: builder.mutation<CreatedEventApiResponse, CreateEventPayload>({
      queryFn: async ({ idempotencyKey, ...data }, api, extraOptions) => {
        const result = await withRetryOn502(
          {
            url: '/event',
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: result.data as CreatedEventApiResponse };
      },
      invalidatesTags: ['ALL_EVENTS', 'UPCOMING_EVENT'],
    }),
    updateEvent: builder.mutation<void, UpdateEventPayload>({
      queryFn: async ({ id, idempotencyKey, ...data }, api, extraOptions) => {
        const result = await withRetryOn502(
          {
            url: `/event/${id}`,
            method: 'PUT',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: undefined };
      },
      invalidatesTags: (_result, _error, { id }) => ['ALL_EVENTS', { type: 'EVENT', id }],
    }),
    getEventQuestions: builder.query<EventQuestionDefinition[], GetEventQuestionsPayload>({
      query: ({ id, assignmentType }) => ({
        url: `/event/${id}/questions`,
        params: { assignmentType },
      }),
      transformResponse: mapEventQuestionsFromApi,
      providesTags: ['EVENT_QUESTIONS'],
    }),
    createEventQuestions: builder.mutation<void, CreateEventQuestionsPayload>({
      query: ({ id, questions }) => ({
        url: `/event/${id}/questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: ['EVENT_QUESTIONS'],
    }),
    createNatteringTopic: builder.mutation<void, CreateEventQuestionsPayload>({
      query: ({ id, questions }) => ({
        url: `/event/${id}/meeting-questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: ['NATTERING_TOPIC'],
    }),
    getNatteringTopics: builder.query<TopicBasicInfo[], WithEventId>({
      query: ({ id }) => `/event/${id}/nattering-questions`,
      providesTags: ['NATTERING_TOPIC'],
    }),
    updateEventAnswers: builder.mutation<void, UpdateEventAnswersPayload>({
      query: ({ id, answers, type }) => ({
        url: `/event/${id}/answers/${type}`,
        method: 'POST',
        data: answers,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'EVENT', id }],
    }),
    deleteEvent: builder.mutation<void, DeleteEventArgs>({
      query: ({ id }) => ({
        url: `/event/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => ['ALL_EVENTS', 'UPCOMING_EVENT', { type: 'EVENT', id }],
    }),
    // Advance sign-up for an upcoming (non-started) event. Sends the confirmation/reminder emails.
    // Requires community membership (403 NOT_COMMUNITY_MEMBER otherwise) — the orchestration in
    // useEnterEvent handles joining the community and retrying.
    registerForEvent: builder.mutation<void, RegisterForEventArgs>({
      query: ({ id }) => ({
        url: `/event/${id}/registrations`,
        method: 'POST',
      }),
      invalidatesTags: (_result, error, { id }) => invalidateEventUnlessNotCommunityMember(error, id),
    }),
    // Live/imminent entry into a started event. Registers attendance WITHOUT sending an email
    // (the core HNS-5 behaviour). Requires community membership, same as registerForEvent.
    attendEvent: builder.mutation<void, AttendEventArgs>({
      query: ({ id }) => ({
        url: `/event/${id}/attendees`,
        method: 'POST',
      }),
      invalidatesTags: (_result, error, { id }) => invalidateEventUnlessNotCommunityMember(error, id),
    }),
    startEvent: builder.mutation<void, StartEventPayload>({
      query: ({ id, ...params }) => ({
        url: `/meeting/${id}/start`,
        method: 'POST',
        data: params,
      }),
      invalidatesTags: (_result, _error, { id }) => ['ALL_EVENTS', 'UPCOMING_EVENT', { type: 'EVENT', id }],
    }),
    stopEvent: builder.mutation<void, WithEventId>({
      query: ({ id }) => ({
        url: `/meeting/${id}/stop`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => ['ALL_EVENTS', 'PAST_EVENTS', { type: 'EVENT', id }],
    }),
    startBroadcast: builder.mutation<void, WithEventId>({
      query: ({ id }) => ({
        url: `/meeting/${id}/start?withBroadcast=1`,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'EVENT', id }],
    }),
    postEventImage: builder.query<EventImageApiResponse, UpdateEventImagePayload>({
      forceRefetch: () => true,
      query: ({ id, ...params }) => ({
        url: `/event/${id}/images`,
        headers: { 'Content-Type': 'multipart/form-data' },
        method: 'POST',
        data: generateFormData(omitUndefined(params)),
      }),
    }),
    getConnections: builder.query<EventGuest[], WithEventId>({
      query: ({ id }) => `/meeting/${id}/myConnection`,
      transformResponse: mapConnectionsFromApi,
    }),
    validateEventPin: builder.query<void, ValidateEventPinPayload>({
      query: ({ id, pin }) => ({ url: `/event/${id}/validate-pin`, params: { pin } }),
    }),
    getTroubleshootingToken: builder.query<{ agoraToken: string; channelName: string }, void>({
      keepUnusedDataFor: 0,
      query: () => `/meeting/connectionTestToken`,
    }),
    getNatalieToken: builder.query<{ agoraToken: string; channelName: string }, void>({
      keepUnusedDataFor: 0,
      queryFn: async (_, api, extraOptions) => {
        // Use the retry wrapper for this specific query
        const result = await staggeredBaseQueryWithRetry(
          {
            url: `/meeting/natalieToken`,
          },
          api,
          extraOptions
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as { agoraToken: string; channelName: string } };
      },
    }),
    getMeetingMetrics: builder.query<MeetingMetricsResponse, WithEventId>({
      query: ({ id }) => `/meeting/${id}/metrics`,
    }),
  }),
});

export const {
  useCreateEventMutation,
  useCreateEventQuestionsMutation,
  useCreateNatteringTopicMutation,
  useGetNatteringTopicsQuery,
  useDeleteEventMutation,
  useGetConnectionsQuery,
  useGetEventDetailsQuery,
  useGetEventGuestsQuery,
  useGetEventGuestsCountQuery,
  useGetEventQuestionsQuery,
  useGetEventsListQuery,
  useGetHasAnyEventQuery,
  useGetTroubleshootingTokenQuery,
  useLazyGetEventQuestionsQuery,
  useGetPublicEventDetailsQuery,
  useLazyValidateEventPinQuery,
  useRegisterForEventMutation,
  useAttendEventMutation,
  useStartBroadcastMutation,
  useStartEventMutation,
  useStopEventMutation,
  useUpdateEventAnswersMutation,
  useUpdateEventMutation,
  useGetMeetingStateQuery,
  useGetNatalieTokenQuery,
  useGetMeetingMetricsQuery,
} = eventApi;
